import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { NgControl, UntypedFormControl } from '@angular/forms';

import { asyncScheduler, BehaviorSubject, combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { filter, map, skip, subscribeOn, take, takeUntil } from 'rxjs/operators';

import { AsyncPipe } from '@angular/common';
import { isEmpty } from 'lodash-es';
import { ActionItem } from '../../../../elements/components/action-item/entities/action-item.entity';
import { ShDroppanelSelectComponent } from '../../../../elements/components/droppanel-select/droppanel-select.component';
import { DroppanelStyle } from '../../../../elements/components/droppanel/entities/droppanel-style.entity';
import { IconSize } from '../../../../elements/components/icon/entities/icon-sizes.entity';
import { ShIconComponent } from '../../../../elements/components/icon/icon.component';
import { ShClickOutsideDirective } from '../../../../elements/directives/click-outside/click-outside.directive';
import { MakeProvider } from '../../../../helpers/base-control-value-accessor';
import { MultiValueFormInput } from '../../../../helpers/multi-value-form-input';
import { DropdownDirection } from '../../../entities/dropdown-direction.entity';
import { FormSelectOption } from '../../../entities/form-select-option.entity';
import { FormValue, FormValueState } from '../../../entities/form-value.entity';
import { MultiFormSelectOptions } from '../../../entities/multi-form-select-options.entity';
import { MultiFormSelectStyle } from '../../../entities/multi-form-select-style.entity';

@Component({
    selector: 'sh-form-multi-select-complex-base',
    templateUrl: './form-multi-select-complex-base.component.html',
    styleUrls: ['./form-multi-select-complex-base.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MakeProvider(ShFormMultiSelectComplexBaseComponent)],
    standalone: true,
    imports: [ShClickOutsideDirective, ShDroppanelSelectComponent, ShIconComponent, AsyncPipe]
})
export class ShFormMultiSelectComplexBaseComponent<T = string>
    extends MultiValueFormInput<T>
    implements OnInit, OnDestroy, AfterViewInit
{
    readonly loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    readonly isOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    readonly state$: ReplaySubject<FormValue<FormSelectOption<T>>[]> = new ReplaySubject<
        FormValue<FormSelectOption<T>>[]
    >(1);

    readonly initialSelectedValues$: ReplaySubject<FormValue<T>[]> = new ReplaySubject<FormValue<T>[]>();
    public selectLabel$: ReplaySubject<string> = new ReplaySubject<string>(1);

    public control?: UntypedFormControl;
    private touched = false;

    public toPropagate$!: Observable<FormValue<T>[]>;
    public sizes: typeof IconSize = IconSize;

    public options$: BehaviorSubject<MultiFormSelectOptions<T> | undefined> = new BehaviorSubject<
        MultiFormSelectOptions<T> | undefined
    >(undefined);

    @HostListener('window:scroll', ['$event'])
    onWindowScroll(event: Event) {
        this.setDropdownClosed();
    }

    /* Control inputs */
    @Input() id!: string;
    @Input() required = false;
    @Input() disabled = false;
    @Input() multiple = false;
    @Input() wrapLabel = false;

    /* Droppanel inputs */
    @Input() height = 300;
    @Input() limitHeightToViewport = false;
    @Input() openDirection: DropdownDirection = DropdownDirection.Below;
    @Input() droppanelStyle: DroppanelStyle = DroppanelStyle.FollowsParentWidth;
    @Input() inOverflownContainer = false;
    @Input() style?: MultiFormSelectStyle = MultiFormSelectStyle.Default;
    @Input() itemCapCount = 50;
    @Input() noResultsActionItem?: ActionItem;
    @Input() sortAlphabetically = false;
    @Input() allowSingleDeselect = true;
    @Input() selectionLimit: number = Number.MAX_SAFE_INTEGER;
    @Input() hiddenItemCount: number = 0; // Items that contribute to the selectCap, but are not present in the component

    /* Search inputs */
    @Input() search = false;
    @Input() autocompleteFct?: (term: string) => Observable<string[]>;

    @Input() set options(options: MultiFormSelectOptions<T>) {
        this.options$.next(options);
    }

    @Input() set isLoading(isLoading: boolean) {
        this.loading$.next(isLoading);
    }

    /* Messages */
    @Input() noResultsFoundMsg!: string;
    @Input() loadingMsg = '';
    @Input() searchPlaceholderMsg!: string;
    @Input() multipleSelectedMsg!: string;
    @Input() allSelectedMsg = '';
    @Input() noneSelectedMsg = '';
    @Input() placeholderMsg?: string;
    @Input() prefixMsg = '';
    @Input() indeterminateMsg = '';
    @Input() listCappedMsg = '';
    @Input() disabledMsg?: string;
    @Input() selectionLimitMessageKey: string = 'ui.selection-limit';

    @Output() searchTerm: EventEmitter<string> = new EventEmitter<string>();
    @Output() shownItemCount: EventEmitter<number> = new EventEmitter<number>();

    constructor(
        protected cd: ChangeDetectorRef,
        protected injector: Injector
    ) {
        super(cd);
    }

    ngOnInit(): void {
        this.initSourceStreams();

        this.initListeners();
        this.initSetTouchedStream();
    }

    ngAfterViewInit(): void {
        const ngControl: NgControl | null = this.injector.get(NgControl, null);
        if (ngControl) {
            this.control = ngControl.control as UntypedFormControl;
        }
    }

    private initSourceStreams(): void {
        this.toPropagate$ = this.buildPropagatableStream();
    }

    // #region initSourceStreams

    private determineSelectLabel(
        formValues: FormValue<FormSelectOption<T>>[],
        options?: MultiFormSelectOptions<T>
    ): string {
        const hasIndeterminate = !!formValues.find(formValue => formValue.state === FormValueState.INDETERMINATE);

        const setFormValues: FormValue<FormSelectOption<T>>[] = formValues.filter(
            formValue => formValue.state === FormValueState.SET
        );
        const count: number = setFormValues.length;

        let label = '';

        if (hasIndeterminate) {
            label = `${this.indeterminateMsg}`;
        } else if (count === 0) {
            label =
                this.selectionLimit === Number.MAX_SAFE_INTEGER
                    ? this.noneSelectedMsg
                    : `${this.noneSelectedMsg} (${count + this.hiddenItemCount}/${this.selectionLimit})`;
        } else if (count === 1) {
            label =
                this.selectionLimit === Number.MAX_SAFE_INTEGER
                    ? setFormValues[0].value.label
                    : `${setFormValues[0].value.label} (${count + this.hiddenItemCount}/${this.selectionLimit})`;
        } else if (!options?.isLiveData && this.allSelectedMsg !== '' && count === options?.options.length) {
            label = this.allSelectedMsg;
        } else {
            const selectCount: string =
                this.selectionLimit === Number.MAX_SAFE_INTEGER
                    ? `(${count + this.hiddenItemCount})`
                    : `(${count + this.hiddenItemCount}/${this.selectionLimit})`;
            label = `${this.multipleSelectedMsg} ${selectCount}`;
        }

        const prefixedLabel: string = [this.prefixMsg, label].filter(n => n).join(': ');

        if (isEmpty(prefixedLabel)) {
            return this.placeholderMsg ? this.placeholderMsg : '';
        }

        return prefixedLabel;
    }

    private buildPropagatableStream(): Observable<FormValue<T>[]> {
        return this.state$.pipe(
            skip(1),
            map(state => this.stateFormSelectOptionsToFormValues(state))
        );
    }

    private stateFormSelectOptionsToFormValues(formValues: FormValue<FormSelectOption<T>>[]): FormValue<T>[] {
        return formValues.map(
            formValueOfFormSelectOption =>
                new FormValue(formValueOfFormSelectOption.value.value, formValueOfFormSelectOption.state)
        );
    }

    // #endregion initSourceStreams

    private initListeners(): void {
        this.initStateChangeListeners();
    }

    // #region initListeners
    private initStateChangeListeners(): void {
        combineLatest([this.state$, this.options$]) // Re-determine label if options have changed
            .pipe(subscribeOn(asyncScheduler))
            .subscribe(([values, options]) => {
                this.selectLabel$.next(this.determineSelectLabel(values, options));
            });

        this.toPropagate$.pipe(subscribeOn(asyncScheduler)).subscribe(values => this.propagateChange(values));
    }

    // #endregion initListeners

    private initSetTouchedStream(): void {
        this.isOpen$
            .pipe(
                filter(isOpen => !isOpen && this.touched),
                take(1),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                if (this.control?.parent && this.control.parent.validator) {
                    this.control.parent.markAllAsTouched();
                } else {
                    this.control?.markAllAsTouched();
                }
                (<EventEmitter<any>>this.control?.statusChanges).emit(this.control?.status);
            });
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();

        this.loading$.complete();
        this.isOpen$.complete();
        this.initialSelectedValues$.complete();
        this.state$.complete();
    }

    public toggleDropdown(): void {
        this.isOpen$.next(!this.isOpen$.value);
        this.touched = true;
    }

    public setDropdownClosed(): void {
        this.isOpen$.next(false);
    }

    protected writeValueImpl(formValues: FormValue<any>[]): void {
        /*  superclass ensures that it's an FormValue<any>[],
            here we need to check that all values are of same type */

        const values: T[] = formValues.map(fv => fv.value);

        const allHaveSameType: boolean = formValues.every(fv => typeof fv.value === typeof formValues[0].value);

        if (!allHaveSameType) {
            console.error('ShFormMultiSelectComplexBaseComponent:: all values must be of same type', values);
            this.initialSelectedValues$.next([]);
            return;
        }

        this.initialSelectedValues$.next(formValues as FormValue<T>[]);
    }

    public onSelectionStateChange(selection: FormValue<FormSelectOption<T>>[]) {
        this.state$.next(selection);
    }

    public onSearch(searchTerm: string) {
        this.searchTerm.emit(searchTerm);
    }

    public onShownItemCountChange(shownItemCount: number): void {
        this.shownItemCount.emit(shownItemCount);
    }

    public get title$(): Observable<string> {
        return this.disabledMsg ? of(this.disabledMsg) : this.selectLabel$;
    }

    protected override propagateDisabledState(state: boolean) {
        // Not needed since the external control is directly injected
    }
}
